import React, { useMemo, useState } from 'react';
import {
  Accordion, AccordionSummary, AccordionDetails, Typography, Paper, Grid, Button,
  TextField, InputAdornment, Icon, Chip, IconButton
} from '@mui/material';
import { ExpandMore, Search, Close, ThumbUpAltOutlined, ThumbUpAlt, ThumbDownAltOutlined, ThumbDownAlt, UnfoldMore, UnfoldLess, HelpOutline, HomeOutlined, Inventory2Outlined, GroupOutlined, TuneOutlined, SettingsOutlined, DescriptionOutlined, BuildOutlined, ForumOutlined, FolderOutlined, AssignmentOutlined, FlightOutlined, HelpCenterOutlined } from '@mui/icons-material';
import { faqnewData as initialFaqData, popoularTopics } from '../spartaFaqsData';

const CATEGORY_ICONS = {
  'Home Page': HomeOutlined,
  'Assets': Inventory2Outlined,
  'User Management': GroupOutlined,
  'Configure': TuneOutlined,
  'Settings': SettingsOutlined,
  'Lease Management': DescriptionOutlined,
  'Maintenance Management': BuildOutlined,
  'CommVerge': ForumOutlined,
  'Records Dataroom': FolderOutlined,
  'Projects Management': AssignmentOutlined,
  'Airworthiness Management': FlightOutlined
};
const getCategoryIcon = (section) => CATEGORY_ICONS[section] || HelpCenterOutlined;

const ACCENT_ROTATION = ['#5B4FCF', '#0F9C95', '#EF8D2F', '#DF5360', '#2F80C7', '#8A5A44'];
const getCategoryAccent = (section, categories) => ACCENT_ROTATION[categories.indexOf(section) % ACCENT_ROTATION.length];

const COLORS = {
  primary: '#5B4FCF',
  primarySoft: '#EFEBFC',
  accent: '#0F9C95',
  accentSoft: '#E7F7F5',
  warn: '#EF8D2F',
  warnSoft: '#FFF3E6',
  danger: '#DF5360',
  dangerSoft: '#FEECEE',
  textMuted: '#6F7890',
  border: '#E5E1F7',
  pageBg: '#F6F5FC'
};

const isHtml = (str) => /<\/?[a-z][\s\S]*>/i.test(str);

const stripHtml = (str = '') => str.replace(/<[^>]*>/g, ' ');

const faqKey = (section, faq) => `${section}::${faq.id}`;

const highlightMatch = (text, term) => {
  if (!term) return text;
  const idx = text.toLowerCase().indexOf(term.toLowerCase());
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <mark style={{ background: COLORS.primarySoft, color: COLORS.primary, padding: 0 }}>{text.slice(idx, idx + term.length)}</mark>
      {text.slice(idx + term.length)}
    </>
  );
};

const FaqNew = () => {
  const [faqnewData] = useState(initialFaqData);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [expandedSections, setExpandedSections] = useState([]);
  const [expandedFaqs, setExpandedFaqs] = useState([]);
  const [feedback, setFeedback] = useState({});

  const categories = useMemo(() => Object.keys(faqnewData), [faqnewData]);

  const filteredData = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return Object.entries(faqnewData).reduce((acc, [section, faqs]) => {
      if (selectedCategory !== 'All' && section !== selectedCategory) return acc;
      const active = faqs.filter((faq) => faq.status !== 0);
      const sorted = [...active].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      const matched = term
        ? sorted.filter((faq) => (
          faq.question.toLowerCase().includes(term) ||
          stripHtml(faq.answer).toLowerCase().includes(term)
        ))
        : sorted;
      if (matched.length) acc[section] = matched;
      return acc;
    }, {});
  }, [faqnewData, searchTerm, selectedCategory]);

  const activeCounts = useMemo(() => (
    Object.fromEntries(categories.map((section) => [
      section,
      faqnewData[section].filter((faq) => faq.status !== 0).length
    ]))
  ), [faqnewData, categories]);

  const totalFaqsAll = useMemo(
    () => Object.values(activeCounts).reduce((sum, count) => sum + count, 0),
    [activeCounts]
  );
  const totalCategoriesAll = categories.length;

  const helpfulVotes = useMemo(
    () => Object.values(feedback).filter((v) => v === 'up').length,
    [feedback]
  );
  const mostPopularCategory = useMemo(() => {
    if (!categories.length) return '—';
    return categories.reduce((best, section) => (
      (activeCounts[section] || 0) > (activeCounts[best] || 0) ? section : best
    ), categories[0]);
  }, [categories, activeCounts]);

  const filteredTotal = useMemo(
    () => Object.values(filteredData).reduce((sum, faqs) => sum + faqs.length, 0),
    [filteredData]
  );
  const filteredCategoriesCount = Object.keys(filteredData).length;

  const lastUpdated = useMemo(
    () => new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }),
    []
  );

  const toggleSection = (section, isExpanded) => {
    setExpandedSections((prev) => (
      isExpanded ? [...prev, section] : prev.filter((s) => s !== section)
    ));
  };

  const toggleFaq = (key, isExpanded) => {
    setExpandedFaqs((prev) => (
      isExpanded ? [...prev, key] : prev.filter((k) => k !== key)
    ));
  };

  const allKeys = useMemo(() => {
    const sectionKeys = Object.keys(filteredData);
    const faqKeys = Object.entries(filteredData).flatMap(([section, faqs]) => faqs.map((faq) => faqKey(section, faq)));
    return { sectionKeys, faqKeys };
  }, [filteredData]);

  const allExpanded = allKeys.sectionKeys.length > 0 &&
    allKeys.sectionKeys.every((s) => expandedSections.includes(s)) &&
    allKeys.faqKeys.every((k) => expandedFaqs.includes(k));

  const handleToggleExpandAll = () => {
    if (allExpanded) {
      setExpandedSections([]);
      setExpandedFaqs([]);
    } else {
      setExpandedSections(allKeys.sectionKeys);
      setExpandedFaqs(allKeys.faqKeys);
    }
  };

  const handleTopicClick = (name) => {
    setSelectedCategory('All');
    setSearchTerm(name);
  };

  const handleFeedback = (key, type) => {
    setFeedback((prev) => ({ ...prev, [key]: prev[key] === type ? null : type }));
  };


  return (
    <div style={{background: COLORS.pageBg, height: 'calc(100vh - 100px)', overflowY: 'auto' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '20px 24px',
          borderRadius: '13px',
          marginBottom: '16px',
          background: `linear-gradient(120deg, ${COLORS.primary} 0%, #7B6FE0 100%)`,
          color: '#ffffff'
        }}
      >
        <div>
          <Typography variant='h5' fontFamily="Roboto-SemiBold !important" sx={{ color: '#ffffff' }}>Frequently Asked Questions</Typography>
          <Typography variant='body2' sx={{ color: 'rgba(255,255,255,0.85)', marginTop: '4px' }}>Find quick answers and guidance across SPARTA modules.</Typography>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <Button variant='contained' sx={{ background: '#ffffff', color: COLORS.primary, '&:hover': { background: '#f0eeff' } }} >Add</Button>
        </div>
      </div>
      <Grid container spacing={2}>
        <Grid size={{ md: 9, xs: 12 }}>
          <Grid container spacing={2}>
            <Grid size={{ md: 12, xs: 12 }}>
              <Paper sx={{ padding: 3, border: `1px solid ${COLORS.border}`,borderRadius:'13px' }}>
                <div style={{ display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'center' }}>
                  <TextField
                    placeholder='Search questions, answers or keywords...'
                    fullWidth
                    size='small'
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    slotProps={{
                      input: {
                        startAdornment: (<InputAdornment position="start"><Icon><Search fontSize='small' /></Icon></InputAdornment>),
                        endAdornment: searchTerm ? (
                          <InputAdornment position="end">
                            <IconButton size='small' onClick={() => setSearchTerm('')}>
                              <Close fontSize='small' />
                            </IconButton>
                          </InputAdornment>
                        ) : undefined
                      }
                    }}
                  />
                  <Button
                    variant='outlined'
                    color='inherit'
                    startIcon={allExpanded ? <UnfoldLess fontSize='small' /> : <UnfoldMore fontSize='small' />}
                    sx={{ marginLeft: '10px', whiteSpace: 'nowrap' }}
                    onClick={handleToggleExpandAll}
                    disabled={allKeys.sectionKeys.length === 0}
                  >
                    {allExpanded ? 'Collapse All' : 'Expand All'}
                  </Button>
                </div>
                <div style={{ padding: '20px 0 5px 0', width: '100%', display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                  <Chip
                    label={`All (${totalFaqsAll})`}
                    variant={selectedCategory === 'All' ? 'filled' : 'outlined'}
                    onClick={() => setSelectedCategory('All')}
                    sx={selectedCategory === 'All' ? { background: COLORS.primary, color: '#FFFFFF' } : { cursor: 'pointer' }}
                  />
                  {categories.map((section) => {
                    const SectionIcon = getCategoryIcon(section);
                    const active = selectedCategory === section;
                    return (
                      <Chip
                        key={section}
                        icon={<SectionIcon fontSize='small' sx={{ color: active ? '#FFFFFF !important' : getCategoryAccent(section, categories) }} />}
                        label={`${section} (${activeCounts[section] || 0})`}
                        variant={active ? 'filled' : 'outlined'}
                        onClick={() => setSelectedCategory(section)}
                        sx={active ? { background: COLORS.primary, color: '#FFFFFF' } : { cursor: 'pointer' }}
                      />
                    );
                  })}
                </div>
              </Paper>
            </Grid>
            <Grid size={{ md: 12, xs: 12 }}>
              <Paper sx={{ border: `1px solid ${COLORS.border}`, overflow: 'auto', height: 'calc(100vh - 315px)',borderRadius:'13px' }}>
                <div style={{ padding: "16px", display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant='body1' fontFamily="Roboto-SemiBold !important">{filteredTotal} FAQs across {filteredCategoriesCount} categories</Typography>
                  <Typography variant='body1' sx={{ color: COLORS.primary }} fontFamily="Roboto-SemiBold !important">Updated {lastUpdated}</Typography>
                </div>
                {
                  Object.keys(filteredData).length === 0 && (
                    <div style={{ padding: '48px 24px', textAlign: 'center' }}>
                      <Search sx={{ fontSize: 32, color: COLORS.border, marginBottom: '8px' }} />
                      <Typography variant='body2' color='textSecondary'>No FAQs match your search.</Typography>
                      {(searchTerm || selectedCategory !== 'All') && (
                        <Button
                          size='small'
                          sx={{ marginTop: '8px', color: COLORS.primary }}
                          onClick={() => { setSearchTerm(''); setSelectedCategory('All'); }}
                        >
                          Clear filters
                        </Button>
                      )}
                    </div>
                  )
                }
                {
                  Object.entries(filteredData).map(([section, faqs]) => {
                    const SectionIcon = getCategoryIcon(section);
                    const accent = getCategoryAccent(section, categories);
                    return (
                      <Accordion
                        key={section}
                        elevation={0}
                        className="accordion-item"
                        square
                        disableGutters
                        expanded={expandedSections.includes(section)}
                        onChange={(e, isExp) => toggleSection(section, isExp)}
                        sx={{
                          borderLeft: `4px solid ${accent}`,
                          borderBottom: `1px solid ${COLORS.border}`,
                          transition: 'background-color 0.2s',
                          '&:hover': { background: COLORS.pageBg },
                          '&:before': { display: 'none' }
                        }}
                      >
                        <AccordionSummary expandIcon={<ExpandMore />}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <SectionIcon fontSize='small' sx={{ color: accent }} />
                            <Typography variant='body1' fontFamily="Roboto-SemiBold !important">{section}</Typography>
                            <Chip label={`${faqs?.length || 0} FAQs`} size='small' sx={{ color: COLORS.primary, background: COLORS.primarySoft }} />
                          </div>
                        </AccordionSummary>
                        <AccordionDetails sx={{ padding: 0 }}>
                          {faqs.map((faq) => {
                            const key = faqKey(section, faq);
                            const isOpen = expandedFaqs.includes(key);
                            return (
                              <div
                                key={faq.id}
                                style={{ borderTop: `1px solid ${COLORS.border}`, padding: '12px 16px 12px 40px', background: isOpen ? COLORS.pageBg : 'transparent' }}
                              >
                                <div
                                  role='button'
                                  tabIndex={0}
                                  onClick={() => toggleFaq(key, !isOpen)}
                                  onKeyDown={(e) => { if (e.key === 'Enter') toggleFaq(key, !isOpen); }}
                                  style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', gap: '8px' }}
                                >
                                  <Typography variant='body2' fontFamily="Roboto-SemiBold !important">{highlightMatch(faq?.question || '', searchTerm)}</Typography>
                                  <ExpandMore fontSize='small' sx={{ color: COLORS.textMuted, transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', flexShrink: 0 }} />
                                </div>
                                {isOpen && (
                                  <div style={{ marginTop: '10px' }}>
                                    {isHtml(faq.answer)
                                      ? <div dangerouslySetInnerHTML={{ __html: faq.answer }} />
                                      : <Typography variant='body2'>{faq.answer}</Typography>}
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '12px' }}>
                                      <Typography variant='body2' sx={{ color: COLORS.textMuted }}>Was this helpful?</Typography>
                                      <IconButton size='small' onClick={() => handleFeedback(key, 'up')}>
                                        {feedback[key] === 'up' ? <ThumbUpAlt fontSize='small' sx={{ color: COLORS.primary }} /> : <ThumbUpAltOutlined fontSize='small' />}
                                      </IconButton>
                                      <IconButton size='small' onClick={() => handleFeedback(key, 'down')}>
                                        {feedback[key] === 'down' ? <ThumbDownAlt fontSize='small' sx={{ color: COLORS.danger }} /> : <ThumbDownAltOutlined fontSize='small' />}
                                      </IconButton>
                                      {feedback[key] && <Typography variant='caption' sx={{ color: COLORS.textMuted }}>Thanks for your feedback!</Typography>}
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </AccordionDetails>
                      </Accordion>
                    );
                  })}
              </Paper>
            </Grid>
          </Grid>
        </Grid>
        <Grid size={{ md: 3, xs: 12 }}>
          <Grid container spacing={2}>
            <Grid size={{ md: 12, xs: 12 }}>
              <Paper sx={{ padding: 2,borderRadius:'13px' }}>
                <Typography variant='body1' fontFamily="Roboto-SemiBold !important">FAQ Overview</Typography>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '14px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', background: COLORS.primarySoft, borderRadius: '10px' }}>
                    <Typography variant='body2' sx={{ color: COLORS.textMuted }}>Total FAQs</Typography>
                    <Typography variant='body1' fontFamily="Roboto-SemiBold !important" sx={{ color: COLORS.primary }}>{totalFaqsAll}</Typography>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', background: COLORS.accentSoft, borderRadius: '10px' }}>
                    <Typography variant='body2' sx={{ color: COLORS.textMuted }}>Categories</Typography>
                    <Typography variant='body1' fontFamily="Roboto-SemiBold !important" sx={{ color: COLORS.accent }}>{totalCategoriesAll}</Typography>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', background: COLORS.warnSoft, borderRadius: '10px', gap: '8px' }}>
                    <Typography variant='body2' sx={{ color: COLORS.textMuted, whiteSpace: 'nowrap' }}>Most viewed category</Typography>
                    <Typography variant='body2' fontFamily="Roboto-SemiBold !important" sx={{ color: COLORS.warn, textAlign: 'right' }} noWrap>{mostPopularCategory}</Typography>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', background: COLORS.dangerSoft, borderRadius: '10px' }}>
                    <Typography variant='body2' sx={{ color: COLORS.textMuted }}>Marked helpful</Typography>
                    <Typography variant='body1' fontFamily="Roboto-SemiBold !important" sx={{ color: COLORS.danger }}>{helpfulVotes}</Typography>
                  </div>
                </div>
              </Paper>
            </Grid>
            <Grid size={{ md: 12, xs: 12 }}>
              <Paper sx={{ padding: 2,borderRadius:'13px' }}>
                <Typography variant='body1' fontFamily="Roboto-SemiBold !important">Popular topics</Typography>
                {popoularTopics?.map((item, idx) => (
                  <div
                    key={idx}
                    onClick={() => handleTopicClick(item?.name)}
                    style={{ display: 'flex', justifyContent: 'space-between', margin: '8px 0 0 0', cursor: 'pointer', padding: '4px 6px', borderRadius: '6px', transition: 'background-color 0.15s' }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = COLORS.primarySoft; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                  >
                    <Typography fontSize='13px' sx={{ '&:hover': { color: COLORS.primary } }}>{item?.name}</Typography>
                    <Typography fontSize='12px'>{item?.views}</Typography>
                  </div>
                ))}
              </Paper>
            </Grid>
            <Grid size={{ md: 12, xs: 12 }}>
              <Paper sx={{ padding: 2, background: `linear-gradient(160deg, ${COLORS.primarySoft} 0%, #ffffff 100%)`, border: `1px solid ${COLORS.border}`, borderRadius: '13px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <HelpOutline fontSize='small' sx={{ color: COLORS.primary }} />
                  <Typography variant='body1' fontFamily="Roboto-SemiBold !important">Still need help?</Typography>
                </div>
                <div style={{ padding: '16px 0 0 0' }}>
                  <Typography variant='body2' > Our support team is here to help with any SPARTA question. </Typography><br />
                  <Button
                    variant='contained'
                    sx={{ background: COLORS.primary }}
                    fullWidth
                    component='a'
                    href='mailto:support@sparta.aero?subject=SPARTA%20Support%20Request'
                  >
                    Contact Support
                  </Button><br />
                  <Typography variant='body2' textAlign='center' mt={2} > support@sparta.aero </Typography>
                </div>
              </Paper>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </div>
  );
};

export default FaqNew;
